import {
  createContext,
  useContext,
  useEffect,
  useState,
  type CSSProperties,
  type PointerEvent as ReactPointerEvent,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';
import { ChevronDown, MousePointerClick, X } from 'lucide-react';
import { cn } from '@/lib/utils';

/**
 * THE portal-wide detail panel: bottom sheet on mobile, right-side drawer on
 * desktop, dismissed by the backdrop, the ✕ button, or Escape. Every V2 page
 * composes its sheet from this shell plus the section/row pieces below so all
 * panels share one look and one set of behaviors:
 *
 *   <DetailSheet onClose={...} widthKey="my-page-sheet-width">
 *     <DetailSheetHeader onClose={...} title={name} badges={<StatusBadge/>} />
 *     <DetailSheetBody>
 *       <ClickToEditHint />
 *       <DetailSection label="Info" accent="text-slate-700" grid defaultOpen>
 *         <FieldRowShell name="Phone">…</FieldRowShell>
 *       </DetailSection>
 *       <SheetFooterNote>Changes save instantly</SheetFooterNote>
 *     </DetailSheetBody>
 *   </DetailSheet>
 */

const MIN_SHEET_WIDTH = 360;

function clampSheetWidth(width: number): number {
  return Math.min(
    Math.max(width, MIN_SHEET_WIDTH),
    Math.round(window.innerWidth * 0.9)
  );
}

export interface DetailSheetProps {
  onClose: () => void;
  /**
   * Every sheet's desktop drawer is drag-resizable from its left edge.
   * With a localStorage key here the dragged width is remembered across
   * opens; without one, resizing lasts only while the sheet is open.
   * Double-click always resets to the default `width`.
   */
  widthKey?: string;
  /** Default desktop width (any CSS length) until the user drags */
  width?: string;
  /**
   * Escape closes the sheet unless this is false — pass false while a
   * nested modal is open so Escape dismisses the modal, not the sheet.
   * (Nested dialogs that preventDefault their Escape are respected either
   * way.)
   */
  closeOnEscape?: boolean;
  children: ReactNode;
}

export function DetailSheet({
  onClose,
  widthKey,
  width = '30rem',
  closeOnEscape = true,
  children,
}: Readonly<DetailSheetProps>) {
  // null = the default `width` prop (no drag yet / after a reset); px once
  // dragged. Persisted only when a widthKey is given.
  const [sheetWidth, setSheetWidth] = useState<number | null>(() => {
    if (!widthKey) return null;
    const stored = Number(localStorage.getItem(widthKey));
    return stored > 0 ? clampSheetWidth(stored) : null;
  });

  const applyWidth = (next: number) => {
    setSheetWidth(next);
    if (widthKey) localStorage.setItem(widthKey, String(next));
  };

  const resetWidth = () => {
    setSheetWidth(null);
    if (widthKey) localStorage.removeItem(widthKey);
  };

  const startResize = (e: ReactPointerEvent) => {
    e.preventDefault();
    const widthAt = (ev: globalThis.PointerEvent) =>
      clampSheetWidth(window.innerWidth - ev.clientX);
    const onMove = (ev: globalThis.PointerEvent) => setSheetWidth(widthAt(ev));
    const onUp = (ev: globalThis.PointerEvent) => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      applyWidth(widthAt(ev));
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
  };

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !e.defaultPrevented && closeOnEscape) {
        onClose();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose, closeOnEscape]);

  // Portaled to <body> so the overlay and drawer always cover the full
  // viewport. Rendered in place, any ancestor that creates a containing
  // block for fixed elements (transform, filter, contain, will-change…)
  // would offset them from the top of the screen.
  return createPortal(
    <div className="fixed inset-0 z-50" role="dialog" aria-modal="true">
      <button
        type="button"
        aria-label="Close"
        onClick={onClose}
        className="absolute inset-0 bg-black/30"
      />
      <div
        style={
          {
            '--sheet-width': sheetWidth != null ? `${sheetWidth}px` : width,
          } as CSSProperties
        }
        className={cn(
          'absolute flex flex-col bg-card shadow-xl',
          // Mobile: bottom sheet; desktop: right drawer
          'inset-x-0 bottom-0 max-h-[85vh] rounded-t-xl',
          'sm:inset-x-auto sm:right-0 sm:top-0 sm:h-full sm:max-h-none sm:w-[var(--sheet-width)] sm:max-w-[90vw] sm:rounded-none'
        )}
      >
        <div
          role="separator"
          aria-orientation="vertical"
          aria-label="Resize details panel"
          title="Drag to resize — double-click to reset"
          onPointerDown={startResize}
          onDoubleClick={resetWidth}
          className="absolute inset-y-0 left-0 z-10 hidden w-1.5 cursor-col-resize hover:bg-primary/30 active:bg-primary/40 sm:block"
        />
        {children}
      </div>
    </div>,
    document.body
  );
}

export interface DetailSheetHeaderProps {
  /** Strings get the standard truncating treatment; nodes render as-is */
  title: ReactNode;
  /** Badge/status row rendered under the title */
  badges?: ReactNode;
  /** Extra header buttons rendered before the ✕ (e.g. a delete icon) */
  actions?: ReactNode;
  onClose: () => void;
  /** Anything else below the badge row (e.g. a header-level error line) */
  children?: ReactNode;
}

export function DetailSheetHeader({
  title,
  badges,
  actions,
  onClose,
  children,
}: Readonly<DetailSheetHeaderProps>) {
  return (
    <div className="flex items-start justify-between gap-3 border-b border-border p-4">
      <div className="min-w-0">
        <h2 className="flex items-center gap-2 font-serif text-lg text-foreground">
          {typeof title === 'string' ? (
            <span className="truncate">{title}</span>
          ) : (
            title
          )}
        </h2>
        {badges && (
          <div className="mt-1 flex flex-wrap items-center gap-2">{badges}</div>
        )}
        {children}
      </div>
      <span className="flex shrink-0 items-center gap-1">
        {actions}
        <button
          type="button"
          onClick={onClose}
          className="rounded-md p-1.5 text-muted-foreground hover:bg-muted hover:text-foreground"
          aria-label="Close details"
        >
          <X className="h-5 w-5" />
        </button>
      </span>
    </div>
  );
}

/** Scroll container for everything under the header */
export function DetailSheetBody({
  children,
  className,
}: Readonly<{ children: ReactNode; className?: string }>) {
  return (
    <div className={cn('min-h-0 flex-1 overflow-y-auto', className)}>
      {children}
    </div>
  );
}

/** One hint bar teaches the whole sheet's editing affordance */
export function ClickToEditHint({
  text = 'Click to edit — Enter / Tab to save, ESC to cancel -- Nhấp để chỉnh sửa. Nhấn Enter hoặc Tab để lưu. Nhấn Esc để hủy.',
}: Readonly<{ text?: string }>) {
  return (
    <p className="flex items-center gap-1.5 border-b border-border/60 bg-muted/30 px-4 py-1.5 text-xs text-muted-foreground">
      <MousePointerClick className="h-3.5 w-3.5 shrink-0" />
      {text}
    </p>
  );
}

/** The standard responsive field grid (auto-fills 15rem columns) */
export function FieldsGrid({
  children,
  className,
}: Readonly<{ children: ReactNode; className?: string }>) {
  return (
    <dl
      className={cn(
        'grid grid-cols-[repeat(auto-fill,minmax(15rem,1fr))] gap-x-6 px-4 pb-3',
        className
      )}
    >
      {children}
    </dl>
  );
}

/**
 * Field row layout for a whole sheet subtree:
 *   'inline'  — label left, value right on one line (the default everywhere)
 *   'stacked' — label above a left-aligned value, form-style (beginner-
 *               friendly; WorkflowV2's account sheet opts in)
 * Row components (FieldRowShell and anything composed from it) read this, so
 * a sheet flips layouts by wrapping its body in <StackedFieldLayout> without
 * touching any row's call site.
 */
const FieldLayoutContext = createContext<'inline' | 'stacked'>('inline');

export function StackedFieldLayout({
  children,
}: Readonly<{ children: ReactNode }>) {
  return (
    <FieldLayoutContext.Provider value="stacked">
      {children}
    </FieldLayoutContext.Provider>
  );
}

export function useFieldLayout(): 'inline' | 'stacked' {
  return useContext(FieldLayoutContext);
}

/**
 * True inside a FieldRowShell value: editable controls (ClickToEdit chips,
 * selects, date buttons, …) stretch to fill the field's column instead of
 * shrinking to their content, so every detail panel's inputs line up at the
 * same width. Toggles and other compact controls ignore it.
 */
const FieldFillContext = createContext(false);

export function useFieldFill(): boolean {
  return useContext(FieldFillContext);
}

/**
 * Wrapper for an editor plus its trailing action icons (copy, reveal, open):
 * a full-width row inside a field row so the editor can fill the column,
 * a content-sized inline row anywhere else (tables, headers).
 */
export function FieldControlRow({
  className,
  children,
}: Readonly<{ className?: string; children: ReactNode }>) {
  const fill = useFieldFill();
  return (
    <span
      className={cn(
        'items-center gap-1',
        fill ? 'flex w-full min-w-0' : 'inline-flex max-w-full',
        className
      )}
    >
      {children}
    </span>
  );
}

/** Small uppercase heading dividing groups of field rows (spans the grid) */
export function FieldGroupHeading({ label }: Readonly<{ label: string }>) {
  return (
    <div className="col-span-full mb-1 mt-4 border-b border-border/60 pb-1 font-serif text-sm text-foreground/80 first:mt-1">
      {label}
    </div>
  );
}

/** Shared field row with the error line below. Inline: label left / value
 *  right. Stacked (see StackedFieldLayout): label above a left-aligned value. */
export function FieldRowShell({
  name,
  error,
  children,
}: Readonly<{ name: string; error?: string | null; children: ReactNode }>) {
  const layout = useFieldLayout();
  if (layout === 'stacked') {
    return (
      <div className="min-w-0 py-2">
        <dt className="mb-1 text-xs font-medium uppercase tracking-wide text-muted-foreground">
          {name}
        </dt>
        <dd className="min-w-0 text-sm text-foreground">
          <FieldFillContext.Provider value>
            {children}
          </FieldFillContext.Provider>
        </dd>
        {error && (
          <p className="mt-1 text-xs text-destructive" role="alert">
            {error}
          </p>
        )}
      </div>
    );
  }
  return (
    <div className="border-b border-border/60 py-2.5">
      <div className="flex items-baseline justify-between gap-4">
        <dt className="shrink-0 text-sm text-muted-foreground">{name}</dt>
        <dd className="min-w-0 flex-1 text-right text-sm text-foreground">
          <FieldFillContext.Provider value>
            {children}
          </FieldFillContext.Provider>
        </dd>
      </div>
      {error && (
        <p className="mt-1 text-right text-xs text-destructive" role="alert">
          {error}
        </p>
      )}
    </div>
  );
}

export interface DetailSectionProps {
  label: ReactNode;
  /** Header text color when open (e.g. 'text-emerald-700'); closed = neutral */
  accent?: string;
  /**
   * Border color class for the marker dot (e.g. 'border-emerald-600');
   * defaults to the current text color. The dot fills while open.
   */
  dotClass?: string;
  /**
   * Custom leading marker replacing the accent dot (e.g. a key icon);
   * pass null for a plain header with no marker.
   */
  icon?: ReactNode;
  /** Right-aligned info before the chevron (e.g. a "3/8" filled count) */
  meta?: ReactNode;
  /** Controlled open state; omit to let the section manage itself */
  open?: boolean;
  onToggle?: () => void;
  /** Uncontrolled initial state */
  defaultOpen?: boolean;
  /** Wrap children in the standard FieldsGrid */
  grid?: boolean;
  className?: string;
  children: ReactNode;
}

/** Collapsible section with the shared dot-marker header */
export function DetailSection({
  label,
  accent,
  dotClass,
  icon,
  meta,
  open,
  onToggle,
  defaultOpen = false,
  grid = false,
  className,
  children,
}: Readonly<DetailSectionProps>) {
  const [selfOpen, setSelfOpen] = useState(defaultOpen);
  const isOpen = open ?? selfOpen;
  const toggle = onToggle ?? (() => setSelfOpen(v => !v));
  return (
    <section className={cn('border-b border-border/60', className)}>
      <button
        type="button"
        onClick={toggle}
        aria-expanded={isOpen}
        className={cn(
          'flex w-full items-center justify-between gap-2 px-4 py-3 font-serif text-base hover:bg-muted/50',
          isOpen && accent ? accent : 'text-foreground'
        )}
      >
        <span className="flex items-center gap-2">
          {icon === undefined ? (
            <span
              className={cn(
                'h-2 w-2 rounded-full border-2',
                dotClass ?? 'border-current',
                isOpen && 'bg-current'
              )}
            />
          ) : (
            icon
          )}
          {label}
        </span>
        <span className="flex items-center gap-2">
          {meta != null && (
            <span className="text-xs font-normal text-muted-foreground">
              {meta}
            </span>
          )}
          <ChevronDown
            className={cn(
              'h-4 w-4 text-muted-foreground transition-transform',
              isOpen && 'rotate-180'
            )}
          />
        </span>
      </button>
      {isOpen && (grid ? <FieldsGrid>{children}</FieldsGrid> : children)}
    </section>
  );
}

/** Static (non-collapsible) section heading with the filled accent dot */
export function DetailSectionHeading({
  label,
  accent,
}: Readonly<{ label: ReactNode; accent?: string }>) {
  return (
    <h3
      className={cn(
        'flex items-center gap-2 px-4 pb-1 pt-4 font-serif text-base',
        accent
      )}
    >
      <span className="h-2 w-2 rounded-full border-2 border-current bg-current" />
      {label}
    </h3>
  );
}

/** Closing note at the bottom of a sheet ("Changes save instantly…") */
export function SheetFooterNote({
  children,
}: Readonly<{ children: ReactNode }>) {
  return (
    <p className="p-4 text-center text-xs text-muted-foreground">{children}</p>
  );
}
